export interface TimelineEra {
  id: string;
  name: string;
}

export interface TimelineEvent {
  year: string;
  title: string;
  era: 'indigenous' | 'colonial' | '19th-century' | '20th-century' | 'modern';
  eraName: string;
  badge: string;
  summary: string;
  description: string;
  significance: string;
  icon: string;
  imageUrl?: string;
  imageCaption?: string;
}

export const TIMELINE_ERAS: TimelineEra[] = [
  { id: 'all', name: 'All Eras' },
  { id: 'indigenous', name: 'Indigenous & Shell Mounds' },
  { id: 'colonial', name: 'French Colonial & Brickmaking' },
  { id: '19th-century', name: 'Chahta-Ima & Civil War Era' },
  { id: '20th-century', name: '1912 Schoolhouse & Timber' },
  { id: 'modern', name: 'Museum Foundation & Today' }
];

export const TIMELINE_EVENTS: TimelineEvent[] = [
  {
    year: 'Pre-1700s',
    title: 'Indigenous Choctaw & Bayou Shell Mounds',
    era: 'indigenous',
    eraName: 'Indigenous Era',
    badge: 'Ancient History',
    icon: 'nature_people',
    summary: 'Centuries of indigenous habitation along the shores and bayous of Lake Pontchartrain.',
    description: 'Centuries before European contact, Native Americans, including ancestors of the Choctaw, lived sustainably along the waterways of Bayou Lacombe and Lake Pontchartrain. Ancient shell middens (mounds) discovered along the bayou demonstrate fishing, hunting, and shellfish harvesting culture that endured for generations.',
    significance: 'Established the initial trade pathways, waterways, and ecological understanding of the Northshore.',
    imageUrl: 'https://images.ctfassets.net/f6ou4743hwzs/1RQvQOYCEdsGyRtqxKLwyz/1e3b05a14f189590a7385b16779e6ed6/Blmchoctaw.jpg',
    imageCaption: 'The rich bayou waterways that sustained Choctaw communities for centuries.'
  },
  {
    year: '1718–1740s',
    title: 'Building New Orleans: Northshore Brick & Timber',
    era: 'colonial',
    eraName: 'Colonial Era',
    badge: 'Colonial Settlement',
    icon: 'foundation',
    summary: 'Bayou Lacombe provides the clay, brick, and rot-resistant cypress that literally built New Orleans.',
    description: 'Following the founding of New Orleans in 1718, French colonists required construction materials capable of surviving subtropical humidity, pests, and rot. Guided by Choctaw knowledge of the Northshore’s dense clay deposits and virgin cypress forests, early settlers established kilns and sawmills along Bayou Lacombe, shipping bricks across the lake via schooners.',
    significance: 'Lacombe’s natural resources formed the structural foundation of early Louisiana architecture, including the French Quarter.',
    imageUrl: 'https://images.ctfassets.net/f6ou4743hwzs/5cfeJ5q2SMgI3Z7IO8JkWQ/d714a076c28c7681342e38e8aa56face/Blmearly.jpg',
    imageCaption: 'Early settlement and trade development along Bayou Lacombe.'
  },
  {
    year: '1850s–1880s',
    title: 'Father Adrien Rouquette — Chahta-Ima ("Like a Choctaw")',
    era: '19th-century',
    eraName: '19th Century',
    badge: 'Cultural Legacy',
    icon: 'auto_stories',
    summary: 'The beloved poet-priest who lived alongside the Choctaw in the piney woods of Lacombe.',
    description: 'Father Adrien-Emmanuel Rouquette, known affectionately as "Chahta-Ima" ("resembling Choctaw"), was a Louisiana poet and missionary priest. Deeply devoted to the Choctaw people, he established several chapels and bush arbors along Bayou Lacombe, learning their language, defending their sovereignty, and recording their customs in celebrated poetry and essays.',
    significance: 'A cornerstone of Lacombe’s bilingual and multicultural identity, bridging European Catholic traditions with Choctaw cultural preservation.',
    imageUrl: 'https://images.ctfassets.net/f6ou4743hwzs/5sUzt4o4BuNjfeqyZ3f8EF/b9fcdbc8848bce9fa798d56d9bf8a2c1/Blmpere.jpg',
    imageCaption: 'Father Adrien Rouquette ("Chahta-Ima"), poet-priest of the Choctaw in Lacombe.'
  },
  {
    year: '1850s–Present',
    title: 'La Toussaint — All Saints’ Day Blessing of the Graves',
    era: '19th-century',
    eraName: 'Living Traditions',
    badge: 'Living Tradition',
    icon: 'local_fire_department',
    summary: 'Lacombe’s world-renowned candlelight vigil blending Choctaw, French, and African-Creole ancestral reverence.',
    description: 'Every November 1st at dusk, families gather at cemeteries throughout Lacombe—including Saint Henry, Ducre, and Toomer cemeteries—to whitewash tombstones, decorate graves with flowers, and illuminate them with hundreds of beeswax candles. The tradition combines French Catholic All Saints’ Day with indigenous Choctaw rituals honoring deceased ancestors.',
    significance: 'Recognized nationally as one of the most poignant and unique folk cultural observances in North America.',
    imageUrl: 'https://images.ctfassets.net/f6ou4743hwzs/25J49fl7nXv1vbAQQ489m6/18bfc00b509745060c5024415a379b5f/AllSaints_2019.webp',
    imageCaption: 'The sacred twilight candlelight vigil during La Toussaint in Lacombe.'
  },
  {
    year: '1912',
    title: 'Construction of the Historic Wooden Schoolhouse',
    era: '20th-century',
    eraName: '20th Century',
    badge: 'Architectural Gem',
    icon: 'school',
    summary: 'The St. Tammany Parish School Board constructs the two-room wooden schoolhouse that now houses the museum.',
    description: 'Constructed in 1912, this rural two-room schoolhouse served the children of Lacombe for decades during the heyday of the regional timber and shipbuilding boom. Built with native heart pine and cypress, high ceilings for natural cooling, and expansive sash windows, the building stands as the oldest remaining wooden schoolhouse in St. Tammany Parish.',
    significance: 'A registered historic landmark preserving early 20th-century rural Louisiana public education architecture.',
    imageUrl: 'https://images.ctfassets.net/f6ou4743hwzs/2xaL3IYyS5iU8bcXoWAOdX/9d1784950839614bbbcaa593c23ee090/whyblm.jpg',
    imageCaption: 'The historic 1912 schoolhouse building on South St. Mary Street.'
  },
  {
    year: '1976',
    title: 'Founding of the Bayou Lacombe Museum',
    era: '20th-century',
    eraName: 'Museum History',
    badge: 'Museum Founding',
    icon: 'museum',
    summary: 'Dedicated community volunteers save the schoolhouse and establish a public museum during the U.S. Bicentennial.',
    description: 'During the 1976 United States Bicentennial celebration, passionate Lacombe civic leaders and historians rallied to preserve the deteriorating 1912 schoolhouse from demolition. Led by local residents, the building was restored, historic artifacts were donated by local families, and the Bayou Lacombe Museum opened its doors to the public.',
    significance: 'Solidified Lacombe’s commitment to preserving its indigenous, rural, and multicultural history for future generations.'
  },
  {
    year: '2020s–Present',
    title: 'Modern Renaissance: Heritage Trails & Digital Preservation',
    era: 'modern',
    eraName: 'Modern Era',
    badge: 'Future Forward',
    icon: 'devices',
    summary: 'Ongoing preservation, native plant gardens, outdoor heritage learning, and state-of-the-art digital exhibits.',
    description: 'Today, the Bayou Lacombe Museum continues its mission with expanded historical collections, community programs, interactive digital guides, and outdoor educational spaces. The museum serves as an indispensable cultural anchor for St. Tammany Parish, Northshore travelers, and students exploring Louisiana’s bayou heritage.',
    significance: 'Championing inclusive storytelling, cultural accessibility, and ecological education for generations to come.'
  }
];
